import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import { Col, Container, Row } from 'react-bootstrap';

import ChatTrade from './chat';
import {
  TradeStateContext,
  initialTradeStateContext
} from '../../../../../../contexts/trade';
import OfferedItems from './offeredItems';
import ReceivedItems from './receivedItems';
import { ItemAchievement } from '../inventory';
import { Card } from '../../../../../../components/card';
import { GameContext } from '../../../../../../contexts/game';
import { PlayerContext } from '../../../../../../contexts/player';
import { PostTradeActionAsync } from '../../../../../../services/trade';
import { GameDocumentContext } from '../../../../../../contexts/game-document';
import { deleteFromStore, putToStore } from '../../../../../../utils/indexedDB';
import { EnterGameButton } from '../../../../../../components/enter-game-button';
import { CastPlayerInventory } from '../../../../../../utils/game-engine/inventory';
import { DisplayLanguageContext } from '../../../../../../contexts/display-languages';
import AchievementDialog from '../../../../../../components/dialog/achievement-dialog';
import { generateTitleById } from '../../../../../../utils/game-document/display-languages';

export const TradeCard: React.FC<{
  item: ItemAchievement;
  isLocked: boolean;
  showModal: () => void;
  clickCard?: () => void;
}> = ({ item, isLocked, clickCard, showModal }) => {
  return (
    <>
      <div
        className={`position-relative`}
        style={{
          height: 'fit-content',
          ...(clickCard !== undefined && {
            cursor: isLocked ? 'not-allowed' : 'pointer'
          })
        }}>
        <div
          className={
            'position-absolute rounded-circle p-1 bg-white d-flex align-items-center justify-content-center cursor-pointer'
          }
          style={{
            zIndex: 2,
            top: '3px',
            right: '3px'
          }}
          onClick={showModal}>
          <span className="material-symbols-outlined">visibility</span>
        </div>
        <Card
          style={{
            ...(isLocked && {
              opacity: 0.5
            }),
            textAlign: 'center'
          }}
          imageUrl={item.imageRes}
          title={`${item.titleRes ?? ''}`}
          subTitle={`(${item.quantity})`}
          {...(!isLocked && {
            onClick: clickCard
          })}
        />
      </div>
    </>
  );
};

export const TradeScreen = () => {
  const [game] = React.useContext(GameContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [trade, setTrade] = React.useContext(TradeStateContext);
  const [player] = React.useContext(PlayerContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);
  const [playerItems, setPlayerItems] = React.useState<ItemAchievement[]>([]);
  const [itemCard, setItemCard] = React.useState<ItemAchievement | undefined>();

  const playerList = game.gameState?.players ?? [];
  const playerCode = player.playerState?.code ?? '';
  const tradeRole = playerCode === trade.offerPlayerCode ? 'offer' : 'respond';
  const tradeStatus =
    tradeRole === 'offer' ? trade.offerPlayerStatus : trade.requestPlayerStatus;

  const tradePlayer =
    playerCode === trade.offerPlayerCode
      ? playerList.find((p) => p.code === trade.requestPlayerCode)
      : playerList.find((p) => p.code === trade.offerPlayerCode);

  const onCancel = async () => {
    if (game.gameCode) {
      try {
        await PostTradeActionAsync(
          game.gameCode,
          String(trade.id),
          playerCode === trade.offerPlayerCode ? 'offer' : 'respond',
          'cancel'
        );
        setTrade(initialTradeStateContext);
        deleteFromStore('trade', game.gameCode);
      } catch (error) {
        console.log(error, '>>>error when cancel trade');
      }
    }
  };

  const updateOfferedItems = (
    items: {
      itemId: string;
      quantity: number;
    }[]
  ) => {
    setTrade((prev) => {
      const result = {
        ...prev,
        offeredItems: items
      };
      putToStore('trade', result);
      return result;
    });
  };

  const onAddItem = (item: ItemAchievement) => {
    const selectedIndex = playerItems.findIndex((i) => i.id === item.id);
    if (selectedIndex !== -1) {
      const items = cloneDeep(playerItems);
      const selectedItem = cloneDeep(playerItems[selectedIndex]);
      if (selectedItem.quantity < 1) return;

      selectedItem.quantity--;
      items.splice(selectedIndex, 1, selectedItem);
      setPlayerItems(items);

      const offeredIndex = trade.offeredItems.findIndex(
        (i) => i.itemId === item.id
      );
      if (offeredIndex !== -1) {
        const offered = cloneDeep(trade.offeredItems);
        const selectedOffered = cloneDeep(trade.offeredItems[offeredIndex]);
        selectedOffered.quantity++;
        offered.splice(offeredIndex, 1, selectedOffered);
        updateOfferedItems(offered);
      } else {
        updateOfferedItems([
          ...trade.offeredItems,
          {
            itemId: selectedItem.id,
            quantity: 1
          }
        ]);
      }
    }
  };

  React.useEffect(() => {
    if (gameDocument.gameDocument && player.playerState) {
      setPlayerItems(
        CastPlayerInventory(gameDocument.gameDocument, player.playerState).map(
          (item) => {
            const offered = trade.offeredItems.find(
              (i) => i.itemId === item.id
            );
            return {
              ...item,
              quantity: offered
                ? item.quantity - offered.quantity
                : item.quantity
            };
          }
        )
      );
    }
  }, [gameDocument.gameDocument, player.playerState, trade.offerPlayerCode]);

  return (
    <>
      <Container
        className={
          'drawer__body d-flex flex-column gap-3 text-center trade-screen'
        }
        style={{ height: '100%' }}
        fluid>
        <div
          className={
            'd-flex align-items-center justify-content-between border-bottom pb-3'
          }>
          <h1>
            {generateTitleById(
              'b2c3d4e5-f6a7-b8c9-d0e1-f2a3b4c5d6e7',
              gameDocument,
              displayLanguageContext.displayLanguageSelected.resources!,
              'game'
            ) || 'Trading with'}{' '}
            {tradePlayer?.name}
          </h1>
          <div
            className={
              'd-flex justify-content-center align-items-center cursor-pointer text-decoration-none'
            }
            onClick={onCancel}>
            <span>
              {generateTitleById(
                'd4090b4d-176f-45b6-96b6-9286f9f59239',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Close'}
            </span>
            <span className={'material-symbols-outlined fw-bold'}>close</span>
          </div>
        </div>

        <div className={'d-flex flex-grow-1'} style={{ minHeight: 0 }}>
          <Row className={'m-0 p-0 w-100'}>
            <Col
              xs={4}
              className={'d-flex flex-column flex-grow-1 gap-2 pl-0 pr-3'}
              style={{
                borderRight: '1px solid black',
                height: '100%',
                overflow: 'hidden'
              }}>
              <div className={'d-flex w-100 justify-content-between'}>
                <span>
                  {generateTitleById(
                    'f90a1b2c-3d4e-5f6a-7b8c-9d0e1f2a3b4c',
                    gameDocument,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Your Inventory'}
                </span>
                <span className={'text-primary'}>Tap item to add</span>
              </div>
              <div
                className={'d-flex flex-wrap gap-2 overflow-auto'}
                style={{ minHeight: 0 }}>
                {playerItems
                  .filter((item) => !item.hideInGame)
                  .filter((item) => item.quantity > 0)
                  .map((item) => (
                    <TradeCard
                      key={item.id}
                      item={item}
                      isLocked={tradeStatus !== 'Draft'}
                      clickCard={() => onAddItem(item)}
                      showModal={() => setItemCard(item)}
                    />
                  ))}
              </div>
            </Col>
            <Col
              xs={8}
              className={'d-flex flex-column flex-grow-1 gap-2 pr-0'}
              style={{ height: '100%', overflow: 'hidden' }}>
              <Row
                className={'d-flex flex-grow-1 m-0 p-0 w-100'}
                style={{
                  height: '70%',
                  minHeight: 0,
                  overflow: 'hidden'
                }}>
                <Col
                  xs={6}
                  className={'d-flex flex-grow-1 flex-column gap-2 pr-2 pl-0'}
                  style={{ height: '100%', overflow: 'hidden' }}>
                  <OfferedItems
                    playerItems={playerItems}
                    showModal={setItemCard}
                    setPlayerItems={setPlayerItems}
                  />
                </Col>
                <Col
                  xs={6}
                  className={'d-flex flex-column gap-2 pl-2 pr-0'}
                  style={{ height: '100%', overflow: 'hidden' }}>
                  <ReceivedItems
                    tradePlayer={tradePlayer}
                    showModal={setItemCard}
                  />
                </Col>
              </Row>

              <ChatTrade tradePlayer={tradePlayer} />
            </Col>
          </Row>
        </div>
      </Container>

      <EnterGameButton />

      {itemCard && (
        <AchievementDialog
          imageUrl={itemCard.imageRes}
          onMaskClick={() => setItemCard(undefined)}
          onClose={() => setItemCard(undefined)}>
          <h1>{itemCard.quantity}</h1>
          <h2>{itemCard.titleRes}</h2>
          <p>{itemCard.summaryRes}</p>
        </AchievementDialog>
      )}
    </>
  );
};
